import type {
	ApiPlayable,
	CmsItem,
	CmsManifestSection,
	CmsPageManifest,
	CmsSection,
	PlayableSummary,
	UserSummary,
} from "@hive/sdk";
type CmsItemsSection = Exclude<CmsSection, { layout: "FEED" }>;

export const EXPLORE_PREVIEW_LIMIT = 24;
export type ExploreManifestSection = Exclude<CmsManifestSection, { layout: "FEED" }>;
export interface ExploreData {
	manifest: CmsPageManifest;
	sections: CmsItemsSection[];
}

export type ExplorePresentation = "hero" | "rail" | "grid";

/** CMS layout alone selects the shelf size, independent of title, position or game grades. */
export function explorePresentation(manifest: CmsPageManifest, id: string): ExplorePresentation {
	const section = manifest.sections.find((entry) => entry.id === id);
	if (section?.layout === "HERO") return "hero";
	return section?.layout === "RAIL" ? "rail" : "grid";
}

/** Card projection only: executable specs are loaded by the player, never by shelves. */
export function summaryPlayable(
	summary: PlayableSummary,
	authors: Record<string, UserSummary>,
): ApiPlayable & { hasAds: boolean; cachePolicy: "default" } {
	return {
		...summary,
		author: summary.authorId ? (authors[summary.authorId] ?? null) : null,
		spec: null,
		remixPrompt: null,
		generationMode: "quick",
		deepSessionId: null,
		publishedJobId: null,
		hasUnpublishedEdits: false,
		syncToComment: false,
		prerollAdEnabled: false,
		adStartDelaySeconds: null,
		// Card summaries never run ads or executable game bundles.
		hasAds: false,
		cachePolicy: "default",
		officialPicked: false,
		deletedAt: null,
		viewerCanRemix: summary.viewerCanRemix ?? null,
	};
}

export function exploreSections(manifest: CmsPageManifest): ExploreManifestSection[] {
	if (
		manifest.page !== "app-explore" ||
		!Number.isFinite(manifest.revision) ||
		!Array.isArray(manifest.sections)
	) {
		throw new Error("Invalid app-explore manifest");
	}
	return (manifest.sections ?? []).filter(
		(section): section is ExploreManifestSection =>
			["HERO", "RAIL", "GRID"].includes(section.layout) &&
			"items" in section &&
			Array.isArray(section.items),
	);
}

export function exploreIds(manifest: CmsPageManifest, limit = EXPLORE_PREVIEW_LIMIT): string[] {
	return [
		...new Set(
			exploreSections(manifest).flatMap((section) =>
				section.items
					.slice(0, limit)
					.flatMap((item) =>
						item.type === "playable" && (item.kind === "game" || item.kind === "drama")
							? [item.playableId]
							: [],
					),
			),
		),
	];
}

/** App-only schemes and executable links are not valid Web destinations. */
export function cmsWebHref(value: string | null | undefined): string | null {
	if (!value || /[\\\u0000-\u0020]/.test(value)) return null;
	if (value.startsWith("/") && !value.startsWith("//")) return value;
	try {
		const url = new URL(value);
		if (!["https:", "http:"].includes(url.protocol)) return null;
		// CMS placements may contain copied production/share URLs. Opening a Muhaha
		// playable must stay on the current deployment, including local previews.
		if (/^(?:(?:www|share)(?:-[a-z0-9-]+)?\.)?muhaha\.ai$/.test(url.hostname) && !url.username && !url.password) {
			const game = /^\/(?:play|p|open\/game)\/([^/]+)\/?$/.exec(url.pathname);
			const drama = /^\/(?:drama|open\/drama)\/([^/]+)\/?$/.exec(url.pathname);
			if (game || drama) return `${game ? "/play/" : "/drama/"}${(game ?? drama)![1]}${url.search}${url.hash}`;
		}
		return url.href;
	} catch {
		return null;
	}
}

export function assembleExploreSection(
	section: ExploreManifestSection,
	playables: Record<string, PlayableSummary>,
	authors: Record<string, UserSummary>,
	limit = EXPLORE_PREVIEW_LIMIT,
): CmsItemsSection {
	const items: CmsItem[] = section.items.slice(0, limit).flatMap((item): CmsItem[] => {
		if (item.type === "creative")
			return item.imageUrl || item.title ? [{ ...item, linkUrl: cmsWebHref(item.linkUrl) }] : [];
		if (item.type !== "playable") return [];
		const summary = playables[item.playableId];
		if (!summary || !["game", "drama"].includes(summary.kind)) return [];
		return [
			{
				type: "playable",
				playable: summaryPlayable(summary, authors),
				title: item.title,
				imageUrl: item.imageUrl,
				linkUrl: cmsWebHref(item.linkUrl),
			},
		];
	});
	return { ...section, items };
}

export function assembleExploreData(
	manifest: CmsPageManifest,
	playables: Record<string, PlayableSummary>,
	authors: Record<string, UserSummary>,
): ExploreData {
	return {
		manifest,
		sections: exploreSections(manifest).map((section) =>
			assembleExploreSection(section, playables, authors),
		),
	};
}
